import { defineStore } from 'pinia'

interface PrintListItem {
  方向: boolean; // true=横向，false=竖向
  记录编号: string;
  设备编号: string;
  环境温度: string;
  校核: string;
  记录: string;
  检验: string;
  计算: string;
  检验日期: string;
  检测原始数据检测项目: Record<string, unknown>;
}

interface PrintListCacheItem {
  data: PrintListItem[];
  timestamp: number;
}

export const useDetailStore = defineStore('detail', {
  state: (): {
    detailParams: { id: string; fromPath: string; timestamp: number };
    analysisPageState: { selectedBusinessId: number | null; timestamp: number };
    printListDataMap: Record<string, PrintListCacheItem>;
    summaryBusinessInfo: { id: number; name: string; code: string } | null;
  } => ({
    // 详情页参数
    detailParams: {
      id: '',
      fromPath: '', // 来源路径
      timestamp: 0 // 时间戳，用于判断参数是否过期
    },
    // 经营项目数据分析页面状态
    analysisPageState: {
      selectedBusinessId: null, // 选中的经营项目ID
      timestamp: 0
    },
    // 打印列表数据缓存，按 cacheKey 隔离，避免不同打印页串数据
    printListDataMap: {},
    // 汇总页面的经营项目信息
    summaryBusinessInfo: null
  }),

  // 持久化存储
  persist: {
    key: 'detail-store',
    storage: sessionStorage
  },

  getters: {
    // 获取详情页ID
    getDetailId: (state) => state.detailParams.id,
    
    // 获取来源路径
    getFromPath: (state) => state.detailParams.fromPath,
    
    // 检查参数是否有效（5分钟内有效）
    isParamsValid: (state) => {
      const now = Date.now()
      const fiveMinutes = 5 * 60 * 1000
      return state.detailParams.id && (now - state.detailParams.timestamp) < fiveMinutes
    }
  },

  actions: {
    // 设置详情页参数
    setDetailParams(id: string, fromPath = '') {
      this.detailParams = {
        id,
        fromPath,
        timestamp: Date.now()
      }
    },

    // 设置经营项目数据分析页面选中状态
    setAnalysisPageState(businessId: number | null) {
      this.analysisPageState = {
        selectedBusinessId: businessId,
        timestamp: Date.now()
      }
    },

    // 获取经营项目数据分析页面选中状态
    getAnalysisPageState() {
      // 5分钟内有效
      const now = Date.now()
      const fiveMinutes = 5 * 60 * 1000
      if (this.analysisPageState.selectedBusinessId && (now - this.analysisPageState.timestamp) < fiveMinutes) {
        return this.analysisPageState.selectedBusinessId
      }
      return null
    },

    // 清除经营项目数据分析页面状态
    clearAnalysisPageState() {
      this.analysisPageState = {
        selectedBusinessId: null,
        timestamp: 0
      }
    },

    // 清除详情页参数
    clearDetailParams() {
      this.detailParams = {
        id: '',
        fromPath: '',
        timestamp: 0
      }
    },

    // 获取详情页参数
    getDetailParams() {
      return this.detailParams
    },

    // 设置打印列表数据
    setPrintListData(cacheKey: string, data: PrintListItem[]) {
      this.printListDataMap[cacheKey] = {
        data,
        timestamp: Date.now()
      }
    },

    // 获取打印列表数据
    getPrintListData(cacheKey: string): PrintListItem[] | null {
      const cacheItem = this.printListDataMap[cacheKey]
      if (cacheItem && cacheItem.data.length > 0) {
        return cacheItem.data
      }
      return null
    },

    // 获取最近一次的打印列表数据，兼容未传 cacheKey 的旧链接
    getLatestPrintListData(): PrintListItem[] | null {
      const cacheKeys = Object.keys(this.printListDataMap)
      if (cacheKeys.length === 0) {
        return null
      }

      const latestKey = cacheKeys.reduce((latest, current) => {
        return this.printListDataMap[current].timestamp > this.printListDataMap[latest].timestamp
          ? current
          : latest
      }, cacheKeys[0])

      return this.printListDataMap[latestKey]?.data?.length
        ? this.printListDataMap[latestKey].data
        : null
    },

    // 清除打印列表数据
    clearPrintListData(cacheKey?: string) {
      if (cacheKey) {
        delete this.printListDataMap[cacheKey]
        return
      }
      this.printListDataMap = {}
    },

    // 设置汇总页面的经营项目信息
    setSummaryBusinessInfo(info: { id: number; name: string; code: string }) {
      this.summaryBusinessInfo = info
    },

    // 获取汇总页面的经营项目信息
    getSummaryBusinessInfo() {
      return this.summaryBusinessInfo
    },

    // 清除汇总页面的经营项目信息
    clearSummaryBusinessInfo() {
      this.summaryBusinessInfo = null
    },

    clearAll() {
      this.clearDetailParams()
      this.clearAnalysisPageState()
      this.clearPrintListData()
      this.clearSummaryBusinessInfo()
    }
  }
})
